// File: src/pages/Reports/AllProductsMovementReport.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../../../axios';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import dayjs from 'dayjs';

export default function AllProductsMovementReport() {
  const companyId = parseInt(localStorage.getItem('companyId'), 10);

  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [stocks, setStocks] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedStock, setSelectedStock] = useState('');

  const [allMovements, setAllMovements] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dateFrom, setDateFrom] = useState(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD')
  );
  const [dateTo, setDateTo] = useState(dayjs().format('YYYY-MM-DD'));

  const fetchProducts = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await axios.get(`/Product/by-company/${companyId}`);
      setProducts(res.data);
    } catch (error) {
      console.error('❌ فشل جلب قائمة الأصناف:', error);
    }
  }, [companyId]);

  const fetchBranches = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await axios.get(`/Branch/by-company/${companyId}`);
      setBranches(res.data);
    } catch (error) {
      console.error('❌ فشل جلب قائمة الفروع:', error);
    }
  }, [companyId]);

  const fetchStocks = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await axios.get(`/Stock/by-company/${companyId}`);
      setStocks(res.data);
    } catch (error) {
      console.error('❌ فشل جلب قائمة المستودعات:', error);
    }
  }, [companyId]);

  const fetchAllMovements = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {
        ...(selectedProduct && { productId: selectedProduct }),
        ...(selectedBranch && { branchId: selectedBranch }),
        ...(selectedStock && { stockId: selectedStock }),
        dateFrom: `${dateFrom}T00:00:00`,
        dateTo: `${dateTo}T23:59:59`,
      };
      const res = await axios.get(`/StockMovements/report/${companyId}`, { params });
      setAllMovements(res.data || []);
    } catch (error) {
      console.error('❌ فشل جلب جميع حركات الأصناف:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedProduct, selectedBranch, selectedStock, dateFrom, dateTo]);

  useEffect(() => {
    fetchProducts();
    fetchBranches();
    fetchStocks();
    fetchAllMovements();
  }, [fetchProducts, fetchBranches, fetchStocks, fetchAllMovements]);

  const rowsToDisplay = useMemo(
    () =>
      allMovements.map((item) => ({
        id: item.id,
        productName: item.productName,
        stockName: item.stockName,
        branchId: item.branchId,
        movementDate: dayjs(item.movementDate).format('YYYY-MM-DD HH:mm'),
        invoiceNumber: item.invoiceNumber || '—',
        invoiceTypeName: item.invoiceTypeName || '—',
        quantityIn: Number(item.quantityIn ?? 0),
        quantityOut: Number(item.quantityOut ?? 0),
        UnitCost: Number(item.unitCost ?? item.UnitCost ?? 0),
        TotalCost: Number(item.totalCost ?? item.TotalCost ?? 0),
        runningBalance: Number(item.runningBalance ?? item.RunningBalance ?? 0),
      })),
    [allMovements]
  );

  const columns = [
    { field: 'productName',     headerName: 'الصنف',            flex: 1, width: 250 },
    { field: 'stockName',       headerName: 'المستودع',         flex: 1, width: 150 },
    { field: 'branchId',        headerName: 'الفرع',            width: 120 },
    { field: 'movementDate',    headerName: 'التاريخ',          width: 160 },
    { field: 'invoiceNumber',   headerName: 'رقم الفاتورة',     width: 100 },
    { field: 'invoiceTypeName', headerName: 'نوع الفاتورة',     width: 100 },
    { field: 'quantityIn',      headerName: 'وارد',             width: 100, type: 'number' },
    { field: 'quantityOut',     headerName: 'صادر',             width: 100, type: 'number' },
    {
      field: 'UnitCost',
      headerName: 'التكلفة',
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2) ?? '0.00',
    },
    {
      field: 'TotalCost',
      headerName: 'الإجمالي',
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2) ?? '0.00',
    },
    {
      field: 'runningBalance',
      headerName: 'الرصيد',
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2) ?? '0.00',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }} dir="rtl">
      <Card>
        <CardHeader title={<Typography variant="h6">تقرير حركة جميع الأصناف</Typography>} />
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select
              label="اختر صنفًا"
              size="small"
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              sx={{ width: 200 }}
            >
              <MenuItem value=""><em>كافة الأصناف</em></MenuItem>
              {products.map((p) => (
                <MenuItem key={p.id} value={p.id.toString()}>{p.arabicName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="اختر فرعًا"
              size="small"
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              sx={{ width: 200 }}
            >
              <MenuItem value=""><em>كافة الفروع</em></MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id.toString()}>{b.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="اختر مستودعًا"
              size="small"
              value={selectedStock}
              onChange={(e) => setSelectedStock(e.target.value)}
              sx={{ width: 200 }}
            >
              <MenuItem value=""><em>كافة المستودعات</em></MenuItem>
              {stocks.map((s) => (
                <MenuItem key={s.id} value={s.id.toString()}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="من تاريخ"
              type="date"
              size="small"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="إلى تاريخ"
              type="date"
              size="small"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" onClick={fetchAllMovements}>عرض التقرير</Button>
          </Box>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rowsToDisplay}
              columns={columns}
              loading={loading}
              components={{ Toolbar: GridToolbar }}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              sx={{ '& .MuiDataGrid-cell': { textAlign: 'center' }, '& .MuiDataGrid-columnHeader': { textAlign: 'center' } }}
            />
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
